/**
 * OaSIS Explorer Frontend - tRPC Client Setup
 * Task 3.3: Advanced Search Page Implementation
 * 
 * Real tRPC client with live backend integration
 */

import { createTRPCReact } from '@trpc/react-query';
import { httpBatchLink } from '@trpc/client';
import type { AppRouter } from '../types/trpc-router';
import { firebaseAuth } from '../config/firebase';

// API base URL - use relative path for production (nginx proxies /api to backend)
// In development, VITE_API_URL should be set to http://localhost:3000
export const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Determine the tRPC endpoint path
// - Production (no VITE_API_URL): use /api/trpc (proxied by nginx to backend:3000/trpc)
// - Development (VITE_API_URL set): use /trpc (direct to backend)
const TRPC_PATH = API_BASE_URL ? '/trpc' : '/api/trpc';

// Create tRPC React client
export const trpc = createTRPCReact<AppRouter>();

/**
 * Create tRPC client with real backend connection
 */
export const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: `${API_BASE_URL}${TRPC_PATH}`,
      
      // Add headers for API requests including auth token
      async headers() {
        const headers: Record<string, string> = {};
        
        // Include Firebase auth token if user is signed in
        const user = firebaseAuth.currentUser;
        if (user) {
          try {
            const token = await user.getIdToken();
            headers['Authorization'] = `Bearer ${token}`;
          } catch {
            // Token refresh failed, continue without auth
          }
        }
        
        return headers;
      },
      
      // Handle fetch errors gracefully
      fetch(url, options) {
        return fetch(url, {
          ...options,
          credentials: 'include',
        });
      },
    }),
  ],
});

/**
 * API hooks for search functionality
 * Now connected to real backend
 */

// Health check hook for testing connection
export const useHealthCheck = () => {
  return trpc.health.useQuery(undefined, {
    refetchInterval: 30000, // Check every 30 seconds
    retry: 1,
  });
};

// Occupation health check
export const useOccupationHealth = (version?: string) => {
  return trpc.occupation.health.useQuery({ version: version ?? '' }, {
    enabled: Boolean(version),
    refetchInterval: 60000, // Check every minute
    retry: 1,
  });
};

